import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  Autocomplete,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { leaveService } from '../../services/leaveService';
import { employeeService } from '../../services/employeeService';
import { projectService } from '../../services/projectService';
import { format } from 'date-fns';

const LeaveForm = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [employeeIdInput, setEmployeeIdInput] = useState('');
  const [formData, setFormData] = useState({
    emp_id: '',
    project_code: '',
    month: '',
    leave_count: 0,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingLeaveId, setExistingLeaveId] = useState(null);

  useEffect(() => {
    loadEmployees();
    loadProjects();
  }, []);

  useEffect(() => {
    // Fetch existing leave count when all required fields are selected
    if (formData.emp_id && formData.project_code && formData.month) {
      fetchExistingLeaveCount();
    }
  }, [formData.emp_id, formData.project_code, formData.month]);

  const loadEmployees = async () => {
    try {
      const data = await employeeService.getActive();
      setEmployees(data);
    } catch (err) {
      setError('Failed to load employees');
    }
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (err) {
      setError('Failed to load projects');
    }
  };

  const fetchExistingLeaveCount = async () => {
    try {
      // Format month as YYYY-MM-01 for API
      const monthDate = format(new Date(formData.month + '-01'), 'yyyy-MM-dd');
      const data = await leaveService.getLeaveCount(formData.emp_id, formData.project_code, monthDate);

      if (data.leave_id) {
        setExistingLeaveId(data.leave_id);
        setFormData(prev => ({ ...prev, leave_count: data.leave_count }));
      } else {
        setExistingLeaveId(null);
        setFormData(prev => ({ ...prev, leave_count: 0 }));
      }
    } catch (err) {
      console.error('Failed to fetch existing leave count:', err);
      setExistingLeaveId(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmployeeIdChange = (e) => {
    const empId = e.target.value;
    setEmployeeIdInput(empId);

    // Find employee by emp_id
    const employee = employees.find(emp => emp.emp_id === empId);
    if (employee) {
      setSelectedEmployee(employee);
      setFormData(prev => ({ ...prev, emp_id: employee.id }));
      setError('');
    } else if (empId) {
      setSelectedEmployee(null);
      setFormData(prev => ({ ...prev, emp_id: '' }));
      setError('Employee ID not found');
    } else {
      setSelectedEmployee(null);
      setFormData(prev => ({ ...prev, emp_id: '' }));
      setError('');
    }
  };

  const handleEmployeeSelect = (event, newValue) => {
    setSelectedEmployee(newValue);
    if (newValue) {
      setEmployeeIdInput(newValue.emp_id);
      setFormData(prev => ({ ...prev, emp_id: newValue.id }));
      setError('');
    } else {
      setEmployeeIdInput('');
      setFormData(prev => ({ ...prev, emp_id: '' }));
    }
  };

  const handleProjectSelect = (event, newValue) => {
    setSelectedProject(newValue);
    if (newValue) {
      setFormData(prev => ({ ...prev, project_code: newValue.project_code }));
      setError('');
    } else {
      setFormData(prev => ({ ...prev, project_code: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.emp_id || !formData.project_code || !formData.month) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.leave_count < 0) {
      setError('Leave count cannot be negative');
      return;
    }

    try {
      setLoading(true);

      // Format month as YYYY-MM-01 for API
      const monthDate = format(new Date(formData.month + '-01'), 'yyyy-MM-dd');

      const payload = {
        emp_id: formData.emp_id,
        project_code: formData.project_code,
        month: monthDate,
        leave_count: parseInt(formData.leave_count),
      };

      await leaveService.create(payload);
      setSuccess(existingLeaveId ? 'Leave updated successfully!' : 'Leave created successfully!');

      // Redirect to leave list after 1.5 seconds
      setTimeout(() => {
        navigate('/leaves');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save leave');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/leaves');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {existingLeaveId ? 'Update Monthly Leave' : 'Add Monthly Leave'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => `${option.project_code} - ${option.name}`}
              value={selectedProject}
              onChange={handleProjectSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project Code"
                  required
                  margin="normal"
                  helperText="Search and select project"
                />
              )}
              isOptionEqualToValue={(option, value) => option.project_code === value?.project_code}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Employee ID"
                  value={employeeIdInput}
                  onChange={handleEmployeeIdChange}
                  margin="normal"
                  helperText="Enter employee ID"
                  error={!!error && error.includes('Employee ID')}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => option.name}
                  value={selectedEmployee}
                  onChange={handleEmployeeSelect}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee Name"
                      required
                      margin="normal"
                      helperText="Search and select employee"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Month / Year"
              name="month"
              type="month"
              value={formData.month}
              onChange={handleChange}
              required
              margin="normal"
              helperText="Select month and year (e.g., Nov-2025)"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              fullWidth
              label="Number of Leaves"
              name="leave_count"
              type="number"
              value={formData.leave_count}
              onChange={handleChange}
              required
              margin="normal"
              helperText={existingLeaveId ? "Updating existing leave count" : "Enter number of leave days for this month"}
              inputProps={{
                min: 0,
                step: 1,
              }}
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
              >
                {loading ? 'Saving...' : (existingLeaveId ? 'Update Leave' : 'Create Leave')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={loading}
                fullWidth
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default LeaveForm;
